<template>
  <f7-popup
    v-model:opened="opened"
    class="main-pc-part-articles-popup"
    close-on-escape
  >
    <!-- Content of the popup -->
    <f7-page v-if="opened">
      <template #fixed>
        <TheNavbar
          :nav-title="$t('common.articles')"
          class="secondary-navbar"
          :popup-close-btn-enable="true"
        />
      </template>

      <UCPreload
        :show="isLoading"
        :number="2"
      />

      <TransitionGroup>
        <template v-if="!isLoading">
          <!-- Real Consumptions -->
          <f7-block>
            <h3>Artículos Reales</h3>
            <div class="container-table">
              <table>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      class="padding-left"
                    >
                      {{ $t('common.warehouse') }}
                    </th>
                    <th scope="col">
                      {{ $t('common.article') }}
                    </th>
                    <th scope="col">
                      {{ $t('common.name') }}
                    </th>
                    <th
                      scope="col"
                      class="padding-right"
                    >
                      {{ $t('common.quantity') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template v-if="realConsumptions.length > 0">
                    <tr
                      v-for="(item, i) in realConsumptions"
                      :key="i"
                    >
                      <td class="padding-left">
                        {{ item.Almacen?.Nombre || '—' }}
                      </td>
                      <td>
                        {{ item.Articulo?.Codigo || '—' }}
                      </td>
                      <td>
                        {{ item.Articulo?.Nombre || '—' }}
                      </td>
                      <td class="padding-right text-align-center">
                        {{ item.Cantidad }}
                      </td>
                    </tr>
                  </template>
                  <!-- No data to show -->
                  <template v-else>
                    <tr>
                      <td
                        colspan="5"
                        class="text-align-center"
                      >
                        No hay datos
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </f7-block>

          <!-- Expected Consumptions -->
          <f7-block>
            <h3>Artículos Previstos</h3>
            <div class="container-table">
              <table>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      class="padding-left"
                    >
                      {{ $t('common.warehouse') }}
                    </th>
                    <th scope="col">
                      {{ $t('common.article') }}
                    </th>
                    <th scope="col">
                      {{ $t('common.name') }}
                    </th>
                    <th
                      scope="col"
                      class="padding-right"
                    >
                      {{ $t('common.quantity') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template v-if="expectedConsumptions.length > 0">
                    <tr
                      v-for="(item, i) in expectedConsumptions"
                      :key="i"
                    >
                      <td class="padding-left">
                        {{ item.Almacen ? item.Almacen.Nombre : '—' }}
                      </td>
                      <td>
                        {{ item.Articulo?.Codigo || '' }}
                      </td>
                      <td>
                        {{ item.Articulo?.Nombre || '' }}
                      </td>
                      <td class="padding-right text-align-center">
                        {{ item.Cantidad }}
                      </td>
                    </tr>
                  </template>
                  <!-- No data to show -->
                  <template v-else>
                    <tr>
                      <td
                        colspan="5"
                        class="text-align-center"
                      >
                        No hay datos
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </f7-block>
        </template>
      </TransitionGroup>
    </f7-page>
  </f7-popup>
</template>

<script setup>
// @ts-check

import { ref, watch } from 'vue';
import { HiNotification } from '@/js/common/HINotificationSupport';
import { callFetchExpectedConsumptions, callFetchRealConsumptions } from '@/js/services/repairs/ApiRepairs';
import { logError } from '@/js/utils/log';

import TheNavbar from '@/components/TheNavbar.vue';
import UCPreload from '@/components/common/UCPreload.vue';

/** @import { Ref } from 'vue' */
/** @import { ParteProduccionRealEntrada, ParteProduccionPrevistoEntrada } from '@erpagroapp/types' */

const props = defineProps({
  workPartId: { type: Number, required: true },
});

const opened = defineModel('opened', {
  type: Boolean,
  default: false,
});

function useRealConsumptions() {
  /** @type {Ref<ParteProduccionRealEntrada[]>}  */
  const realConsumptions = ref([]);

  /**
   * Fetches real consumption articles.
   * @returns {Promise<boolean>} `true` if successful, `false` otherwise.
   */
  async function fetchRealConsumptions() {
    let successful = false;

    try {
      const { data } = await callFetchRealConsumptions(props.workPartId);

      if (Array.isArray(data)) {
        realConsumptions.value = data;
        successful = true;
      } else {
        HiNotification.Toast('No se han podido obtener los artículos reales');
      }
    } catch (error) {
      logError(error);
      HiNotification.Toast('No se han podido obtener los artículos reales');
    }

    return successful;
  }

  watch(opened, () => {
    if (!opened.value) {
      realConsumptions.value = [];
    }
  });

  return {
    realConsumptions,
    fetchRealConsumptions,
  };
}

function useExpectedConsumptions() {
  /** @type {Ref<ParteProduccionPrevistoEntrada[]>}  */
  const expectedConsumptions = ref([]);

  /**
   * Fetches expected consumption articles.
   * @returns {Promise<boolean>} `true` if successful, `false` otherwise.
   */
  async function fetchExpectedConsumptions() {
    let successful = false;

    try {
      const { data } = await callFetchExpectedConsumptions(props.workPartId);

      if (Array.isArray(data)) {
        expectedConsumptions.value = data;
        successful = true;
      } else {
        HiNotification.Toast('No se han podido obtener los artículos previstos');
      }
    } catch (error) {
      logError(error);
      HiNotification.Toast('No se han podido obtener los artículos previstos');
    }

    return successful;
  }

  watch(opened, () => {
    if (!opened.value) {
      expectedConsumptions.value = [];
    }
  });

  return {
    expectedConsumptions,
    fetchExpectedConsumptions,
  };
}

/**
 * @param {object} options
 * @param {() => void} options.fetchRealConsumptions - Function to fetch real consumptions data.
 * @param {() => void} options.fetchExpectedConsumptions - Function to fetch expected consumptions data.
 */
function useLoading({ fetchRealConsumptions, fetchExpectedConsumptions }) {
  const isLoading = ref(false);

  watch(opened, async () => {
    if (opened.value) {
      isLoading.value = true;

      await Promise.all([
        fetchRealConsumptions(),
        fetchExpectedConsumptions(),
      ]);

      isLoading.value = false;
    }
  });

  return {
    isLoading,
  };
}

const {
  realConsumptions,
  fetchRealConsumptions,
} = useRealConsumptions();

const {
  expectedConsumptions,
  fetchExpectedConsumptions,
} = useExpectedConsumptions();

const { isLoading } = useLoading({
  fetchRealConsumptions,
  fetchExpectedConsumptions,
});
</script>

<style scoped lang="scss">
.main-pc-part-articles-popup {
  .container-table {
    overflow-x: auto;

    table {
      width: 100%;
      border-collapse: collapse;
      border-spacing: 0;

      thead {
        background-color: #9e9e9e;

        th {
          background-color: #9e9e9e;
          padding: 5px;
        }

        th:first-child {
          border-top-left-radius: 5px;
        }

        th:last-child {
          border-top-right-radius: 5px;
        }
      }

      tbody {
        background-color: #e6e6e6;

        td {
          padding: 5px;
        }

        td:first-child,
        td:nth-child(3) {
          word-break: break-all;
        }
      }
    }
  }

  .v-enter-active,
  .v-leave-active {
    transition: opacity 0.5s ease;
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }
}
</style>
